// Global
import { sendGTMEvent } from '@next/third-parties/google';
import React, { ButtonHTMLAttributes, forwardRef, JSX } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { SvgIcon } from 'helpers/SvgIcon';
import { GtmEvent } from 'lib/utils/gtm-utils';
import { StyleParamRecord } from 'lib/utils/style-param-utils';
import {
  CtaElements,
  CtaIconAlignments,
  CtaIcons,
  CtaStyleProperties,
  CtaVariants,
  CtaVisibility,
  CtaIconSizes,
} from 'lib/utils/style-param-utils/modules/ctas';

import { CtaComponentClass } from 'lib/hooks/theming/useCtaComponentClass';
import { RemixIcon } from 'helpers/RemixIcon';

type CtaPropsBase = {
  ctaStyle?: StyleParamRecord<CtaElements, CtaStyleProperties>;
  ctaIcon?: CtaIcons;
  ctaIconAlignment?: CtaIconAlignments;
  ctaVisibility?: CtaVisibility;
  ctaIconSize?: CtaIconSizes;
};

// ctaComponentClass is optional for custom buttons
type CtaPropsCustom = CtaPropsBase & {
  ctaVariant: 'custom';
  /** This is required.  We can set to default explicitly if not present in design. */
  ctaComponentClass?: undefined;
};

// ctaComponentClass is required for normal buttons
type CtaPropsNormal = CtaPropsBase & {
  ctaVariant?: CtaVariants | 'custom';
  /** This is required.  We can set to default explicitly if not present in design. */
  ctaComponentClass: CtaComponentClass | 'default';
};

export type CtaProps = CtaPropsCustom | CtaPropsNormal;

export type ButtonWrapperProps = ButtonHTMLAttributes<HTMLButtonElement> &
  CtaProps &
  React.PropsWithChildren & {
    className?: string;
    gtmEvent?: GtmEvent;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    text?: string;
    remixIcon?: string;
    remixIconSize?: 'sm' | 'md' | 'lg' | 'xl';
  };

const ButtonWrapper = forwardRef<HTMLButtonElement, ButtonWrapperProps>(
  (
    {
      className,
      ctaComponentClass,
      ctaStyle,
      ctaIcon: ctaIconOverride,
      ctaIconAlignment: ctaIconAlignmentOverride,
      ctaVariant: ctaVariantOverride,
      ctaVisibility: _ctaVisibility,
      ctaIconSize = 's',
      gtmEvent,
      onClick,
      text,
      children,
      remixIcon,
      remixIconSize = 'md',
      ...props
    }: ButtonWrapperProps,
    ref
  ): JSX.Element | null => {
    const ctaIcon = ctaIconOverride ?? ctaStyle?.ctaIcon;
    const ctaVariant = ctaVariantOverride ?? ctaStyle?.ctaVariant ?? 'primary';
    const ctaIconAlignment = ctaIconAlignmentOverride ?? ctaStyle?.ctaIconAlignment ?? 'right';

    if (ctaComponentClass === 'default') {
      ctaComponentClass =
        ctaVariant === 'primary'
          ? 'component-hero-button-color-1'
          : 'component-hero-button-color-2';
    }

    const { base, icon } = ctaTailwindVariant({
      className: className,
      iconAlignment: ctaIconAlignment,
      variant: ctaVariant,
      style: ctaVariant === 'custom' ? 'primary' : ctaVariant,
    });

    /*
     * EVENT HANDLERS
     */

    const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (gtmEvent) {
        sendGTMEvent({ ...gtmEvent });
      }

      if (onClick) {
        onClick(e);
      }
    };

    /*
     * RENDERING
     */

    // If no content is present, don't print
    if (!text && !children) return <></>;

    return (
      <button
        className={[className, base()].join(' ')}
        onClick={handleOnClick}
        ref={ref}
        {...props}
        aria-label={text}
      >
        {text}
        {children}
        {ctaIcon && <SvgIcon className={icon()} icon={ctaIcon} size={ctaIconSize} />}
        {remixIcon && <RemixIcon className={icon()} name={remixIcon} size={remixIconSize} />}
      </button>
    );
  }
);

ButtonWrapper.displayName = 'ButtonWrapper';

export default ButtonWrapper;

export const ctaTailwindVariant = tv({
  slots: {
    base: [],
    icon: [],
  },
  variants: {
    iconAlignment: {
      left: {
        base: ['flex-row'],
      },
      right: {
        base: ['flex-row'],
      },
    },
    variant: {
      custom: {
        base: [],
      },
      primary: {
        base: [
          'group',
          'flex',
          'items-center',
          'justify-center',
          'gap-x-spacing-spacing-8',
          'w-full',
          'md:w-auto',
          'min-w-[7.5rem]',
          'min-h-12',
          'px-spacing-spacing-24',
          'py-spacing-spacing-16',
          'text-center',
          'text-typography-font-size-body-medium',
          'font-semibold',
          'leading-typography-line-height-xxsmall',
          'border-general-border-width-button',
          'rounded-general-border-radius-button',
          'disabled:no-underline',
          'disabled:opacity-50',
          'focus:outline-none',
          'focus:ring-4',
          'focus:ring-offset-0',
          'focus:ring-color-core-color-4-SkyBlue',
          'hover:no-underline',
          'transition-colors',
          'duration-300',
          'font-typography-font-family-body',
        ],
      },
      primaryInverse: {
        base: [
          'group',
          'flex',
          'items-center',
          'justify-center',
          'gap-x-spacing-spacing-8',
          'w-full',
          'md:w-auto',
          'min-w-[7.5rem]',
          'min-h-12',
          'px-spacing-spacing-24',
          'py-spacing-spacing-16',
          'text-center',
          'text-typography-font-size-body-medium',
          'font-semibold',
          'leading-typography-line-height-xxsmall',
          'border-general-border-width-button',
          'rounded-general-border-radius-button',
          'disabled:no-underline',
          'disabled:opacity-50',
          'focus:outline-none',
          'focus:ring-4',
          'focus:ring-offset-0',
          'focus:ring-color-core-color-4-SkyBlue',
          'hover:no-underline',
          'transition-colors',
          'duration-300',
          'font-typography-font-family-body',
        ],
      },
      secondary: {
        base: [
          'group',
          'flex',
          'items-center',
          'justify-center',
          'gap-x-spacing-spacing-8',
          'w-full',
          'md:w-auto',
          'min-w-[7.5rem]',
          'min-h-12',
          'px-spacing-spacing-24',
          'py-spacing-spacing-16',
          'text-center',
          'text-typography-font-size-body-medium',
          'font-semibold',
          'leading-typography-line-height-xxsmall',
          'border-general-border-width-button',
          'rounded-general-border-radius-button',
          'disabled:no-underline',
          'disabled:opacity-50',
          'focus:outline-none',
          'focus:ring-4',
          'focus:ring-offset-0',
          'focus:ring-color-core-color-4-SkyBlue',
          'hover:no-underline',
          'transition-colors',
          'duration-300',
          'font-typography-font-family-body',
        ],
      },
      secondaryInverse: {
        base: [
          'group',
          'flex',
          'items-center',
          'justify-center',
          'gap-x-spacing-spacing-8',
          'w-full',
          'md:w-auto',
          'min-w-[7.5rem]',
          'min-h-12',
          'px-spacing-spacing-24',
          'py-spacing-spacing-16',
          'text-center',
          'text-typography-font-size-body-medium',
          'font-semibold',
          'leading-typography-line-height-xxsmall',
          'border-general-border-width-button',
          'rounded-general-border-radius-button',
          'disabled:no-underline',
          'disabled:opacity-50',
          'focus:outline-none',
          'focus:ring-4',
          'focus:ring-offset-0',
          'focus:ring-color-core-color-4-SkyBlue',
          'hover:no-underline',
          'transition-colors',
          'duration-300',
          'font-typography-font-family-body',
        ],
      },
      primaryOnCanvas: {
        base: [
          'group',
          'flex',
          'items-center',
          'justify-center',
          'gap-x-spacing-spacing-8',
          'w-full',
          'md:w-auto',
          'min-w-[7.5rem]',
          'min-h-12',
          'px-spacing-spacing-24',
          'py-spacing-spacing-16',
          'text-center',
          'text-typography-font-size-body-medium',
          'font-semibold',
          'leading-typography-line-height-xxsmall',
          'border-general-border-width-button',
          'rounded-general-border-radius-button',
          'disabled:no-underline',
          'disabled:opacity-50',
          'focus:outline-none',
          'focus:ring-4',
          'focus:ring-offset-0',
          'focus:ring-color-core-color-4-SkyBlue',
          'hover:no-underline',
          'transition-colors',
          'duration-300',
          'font-typography-font-family-body',
        ],
      },
      tertiary: {
        base: [
          'group',
          'flex',
          'items-center',
          'focus:outline-none',
          'focus:ring-4',
          'focus:ring-offset-0',
          'focus:ring-color-core-color-4-SkyBlue',
          'gap-x-spacing-spacing-8',
          'font-semibold',
          'rounded-general-border-radius-none',
          'p-spacing-spacing-2',
          'font-typography-font-family-body',
          'disabled:no-underline',
          'disabled:opacity-50',
        ],
      },
      tertiaryInverse: {
        base: [
          'group',
          'flex',
          'items-center',
          'focus:outline-none',
          'focus:ring-4',
          'focus:ring-offset-0',
          'focus:ring-color-core-color-4-SkyBlue',
          'gap-x-spacing-spacing-8',
          'font-semibold',
          'rounded-general-border-radius-none',
          'p-spacing-spacing-2',
          'font-typography-font-family-body',
          'disabled:no-underline',
          'disabled:opacity-50',
        ],
      },
    },
    visibility: {
      visible: {
        base: [],
      },
      hidden: {
        base: ['hidden'],
      },
    },
    style: {
      primary: {
        base: [],
      },
      primaryInverse: {
        base: [],
      },
      secondary: {
        base: [],
      },
      secondaryInverse: {
        base: [],
      },
      primaryOnCanvas: {
        base: [],
      },
      tertiary: {
        base: [],
      },
      tertiaryInverse: {
        base: [],
      },
    },
  },
  compoundVariants: [
    {
      variant: 'primary',
      style: [
        'primary',
        'primaryInverse',
        'secondary',
        'secondaryInverse',
        'primaryOnCanvas',
        'tertiary',
        'tertiaryInverse',
      ],
      class: {
        base: [
          'bg-component-button-primary-bg',
          'border-component-button-primary-border',
          'text-component-button-primary-text',
          'hover:bg-component-button-primary-bg-hover',
          'hover:border-component-button-primary-border-hover',
          'hover:text-component-button-primary-text-hover',
          'focus:bg-component-button-primary-bg-focused',
          'focus:border-component-button-primary-border-focused',
          'focus:text-component-button-primary-text-focused',
          'disabled:bg-component-button-primary-bg-disabled',
          'disabled:border-component-button-primary-border-disabled',
          'disabled:text-component-button-primary-text-disabled',
        ],
        icon: [
          'text-component-button-primary-icon',
          'group-hover:text-component-button-primary-icon-hover',
          'group-focus:text-component-button-primary-icon-focused',
          'group-disabled:text-component-button-primary-icon-disabled',
        ],
      },
    },

    {
      variant: 'primaryInverse',
      style: [
        'primary',
        'primaryInverse',
        'secondary',
        'secondaryInverse',
        'primaryOnCanvas',
        'tertiary',
        'tertiaryInverse',
      ],
      class: {
        base: [
          'bg-component-button-primary-inverse-bg',
          'border-component-button-primary-inverse-border',
          'text-component-button-primary-inverse-text',
          'hover:bg-component-button-primary-inverse-bg-hover',
          'hover:border-component-button-primary-inverse-border-hover',
          'hover:text-component-button-primary-inverse-text-hover',
          'focus:bg-component-button-primary-inverse-bg-focused',
          'focus:border-component-button-primary-inverse-border-focused',
          'focus:text-component-button-primary-inverse-text-focused',
          'disabled:bg-component-button-primary-inverse-bg-disabled',
          'disabled:border-component-button-primary-inverse-border-disabled',
          'disabled:text-component-button-primary-inverse-text-disabled',
        ],
        icon: [
          'text-component-button-primary-inverse-icon',
          'group-hover:text-component-button-primary-inverse-icon-hover',
          'group-focus:text-component-button-primary-inverse-icon-focused',
          'group-disabled:text-component-button-primary-inverse-icon-disabled',
        ],
      },
    },

    {
      variant: 'secondary',
      style: [
        'primary',
        'primaryInverse',
        'secondary',
        'secondaryInverse',
        'primaryOnCanvas',
        'tertiary',
        'tertiaryInverse',
      ],
      class: {
        base: [
          'bg-transparent',
          'border-component-button-secondary-border',
          'text-component-button-secondary-text',
          'hover:bg-component-button-secondary-bg-hover',
          'hover:border-component-button-secondary-bg-hover',
          'hover:text-component-button-secondary-text-hover',
          'focus:bg-component-button-secondary-bg-focused',
          'focus:border-component-button-secondary-bg-focused',
          'focus:text-component-button-secondary-text-focused',
          'disabled:bg-component-button-secondary-bg-disabled',
          'disabled:border-component-button-secondary-border-disabled',
          'disabled:text-component-button-secondary-text-disabled',
        ],
        icon: [
          'text-component-button-secondary-icon',
          'group-hover:text-component-button-secondary-icon-hover',
          'group-focus:text-component-button-secondary-icon-focused',
          'group-disabled:text-component-button-secondary-icon-disabled',
        ],
      },
    },

    {
      variant: 'secondaryInverse',
      style: [
        'primary',
        'primaryInverse',
        'secondary',
        'secondaryInverse',
        'primaryOnCanvas',
        'tertiary',
        'tertiaryInverse',
      ],
      class: {
        base: [
          'bg-component-button-secondary-inverse-bg',
          'border-component-button-secondary-inverse-border',
          'text-component-button-secondary-inverse-text',
          'hover:bg-component-button-secondary-inverse-bg-hover',
          'hover:border-component-button-secondary-inverse-border-hover',
          'hover:text-component-button-secondary-inverse-text-hover',
          'focus:bg-component-button-secondary-inverse-bg-focused',
          'focus:border-component-button-secondary-inverse-border-focused',
          'focus:text-component-button-secondary-inverse-text-focused',
          'disabled:bg-component-button-secondary-inverse-bg-disabled',
          'disabled:border-component-button-secondary-inverse-border-disabled',
          'disabled:text-component-button-secondary-inverse-text-disabled',
        ],
        icon: [
          'text-component-button-secondary-inverse-icon',
          'group-hover:text-component-button-secondary-inverse-icon-hover',
          'group-focus:text-component-button-secondary-inverse-icon-focused',
          'group-disabled:text-component-button-secondary-inverse-icon-disabled',
        ],
      },
    },

    {
      variant: 'primaryOnCanvas',
      style: [
        'primary',
        'primaryInverse',
        'secondary',
        'secondaryInverse',
        'primaryOnCanvas',
        'tertiary',
        'tertiaryInverse',
      ],
      class: {
        base: [
          'bg-component-button-primary-onCanvas-bg',
          'border-component-button-primary-onCanvas-border',
          'text-component-button-primary-onCanvas-text',
          'hover:bg-component-button-primary-onCanvas-bg-hover',
          'hover:border-component-button-primary-onCanvas-border-hover',
          'hover:text-component-button-primary-onCanvas-text-hover',
          'focus:bg-component-button-primary-onCanvas-bg-focused',
          'focus:border-component-button-primary-onCanvas-border-focused',
          'focus:text-component-button-primary-onCanvas-text-focused',
          'disabled:bg-component-button-primary-onCanvas-bg-disabled',
          'disabled:border-component-button-primary-onCanvas-border-disabled',
          'disabled:text-component-button-primary-onCanvas-text-disabled',
        ],
        icon: [
          'text-component-button-primary-onCanvas-icon',
          'group-hover:text-component-button-primary-onCanvas-icon-hover',
          'group-focus:text-component-button-primary-onCanvas-icon-focused',
          'group-disabled:text-component-button-primary-onCanvas-icon-disabled',
        ],
      },
    },

    {
      variant: 'tertiary',
      style: [
        'primary',
        'primaryInverse',
        'secondary',
        'secondaryInverse',
        'primaryOnCanvas',
        'tertiary',
        'tertiaryInverse',
      ],
      class: {
        base: [
          'text-component-button-tertiary-text',
          'hover:text-component-button-tertiary-text-hover',
          'hover:underline',
          'focus:bg-component-button-tertiary-bg-focused',
          'focus:text-component-button-tertiary-text-focused',
          'focus:underline',
          'disabled:text-component-button-tertiary-text-disabled',
        ],
        icon: [
          'text-component-button-tertiary-icon',
          'group-hover:text-component-button-tertiary-icon-hover',
          'group-focus:text-component-button-tertiary-icon-focused',
          'group-disabled:text-component-button-tertiary-icon-disabled',
        ],
      },
    },

    {
      variant: 'tertiaryInverse',
      style: [
        'primary',
        'primaryInverse',
        'secondary',
        'secondaryInverse',
        'primaryOnCanvas',
        'tertiary',
        'tertiaryInverse',
      ],
      class: {
        base: [
          'text-component-button-tertiary-inverse-text',
          'hover:text-component-button-tertiary-inverse-text-hover',
          'hover:underline',
          'focus:bg-component-button-tertiary-inverse-bg-focused',
          'focus:text-component-button-tertiary-inverse-text-focused',
          'focus:underline',
          'disabled:text-component-button-tertiary-inverse-text-disabled',
        ],
        icon: [
          'text-component-button-tertiary-inverse-icon',
          'group-hover:text-component-button-tertiary-inverse-icon-hover',
          'group-focus:text-component-button-tertiary-inverse-icon-focused',
          'group-disabled:text-component-button-tertiary-inverse-icon-disabled',
        ],
      },
    },
  ],
});
